import { useEffect, useRef, useState } from 'react';
import { clearTab, markUpdate, NO_BLINK, type BlinkState, type QueueTab } from './lib/queueBlink';
import type { QueueSignals } from './lib/useQueueSignals';

/**
 * Stacked-tabs fallback for the operations panel's protected queues
 * (control-panel spec §2). One full-width queue shows at a time; the inactive
 * tab BLINKS when its queue gets an update so a hidden-queue update is never
 * missed (see `queueBlink.ts` for the state math, `useQueueSignals.ts` for the
 * update signals).
 *
 * This is the NARROW-WIDTH fallback (#211) — the panel defaults to side-by-side
 * queues and drops to tabs only when the measured panel width can't fit two
 * readable columns (`queueLayoutForWidth`). Both queues stay MOUNTED (the inactive
 * one is hidden, not unmounted) so each keeps polling and its scroll position —
 * only the blink draws attention to a hidden update.
 *
 * Blink colour follows Colour Law: the Urgent Alert tab pulses URGENT-RED, the
 * Assist tab a steadier violet accent (its standing queue colour). Motion is
 * bounded — only a hidden tab with an unseen update animates.
 */

export function StackedQueueTabs({
  alert,
  assist,
  signals,
}: {
  alert: JSX.Element;
  assist: JSX.Element;
  signals: QueueSignals;
}): JSX.Element {
  const [activeTab, setActiveTab] = useState<QueueTab>('alert');
  const [blink, setBlink] = useState<BlinkState>(NO_BLINK);
  // Last signal counters seen — a change is one update tick for that queue.
  const lastSignals = useRef<QueueSignals>(signals);
  // Read the active tab inside the signal effect without re-subscribing on tab
  // change (which would re-run the diff and mis-fire a stale tick).
  const activeRef = useRef<QueueTab>(activeTab);
  activeRef.current = activeTab;

  useEffect(() => {
    setBlink((prev) => {
      let next = prev;
      if (signals.alert !== lastSignals.current.alert) {
        next = markUpdate(next, 'alert', activeRef.current);
      }
      if (signals.assist !== lastSignals.current.assist) {
        next = markUpdate(next, 'assist', activeRef.current);
      }
      lastSignals.current = signals;
      return next;
    });
  }, [signals]);

  const select = (tab: QueueTab): void => {
    setActiveTab(tab);
    setBlink((prev) => clearTab(prev, tab));
  };

  return (
    <section
      data-testid="queue-tabs"
      data-active-tab={activeTab}
      className="flex h-full min-h-0 flex-col overflow-hidden rounded-lg border border-slate-700"
    >
      <div role="tablist" className="flex shrink-0 border-b border-slate-700 bg-slate-900/80">
        <TabButton
          tab="alert"
          label="Urgent Alerts"
          active={activeTab === 'alert'}
          blinking={blink.alert}
          onSelect={select}
        />
        <TabButton
          tab="assist"
          label="Assist"
          active={activeTab === 'assist'}
          blinking={blink.assist}
          onSelect={select}
        />
      </div>
      {/* Both queues stay mounted; the inactive one is hidden so it keeps polling
          and its scroll position. */}
      <div className={activeTab === 'alert' ? 'min-h-0 flex-1 overflow-hidden' : 'hidden'}>
        {alert}
      </div>
      <div className={activeTab === 'assist' ? 'min-h-0 flex-1 overflow-hidden' : 'hidden'}>
        {assist}
      </div>
    </section>
  );
}

function TabButton({
  tab,
  label,
  active,
  blinking,
  onSelect,
}: {
  tab: QueueTab;
  label: string;
  active: boolean;
  blinking: boolean;
  onSelect: (tab: QueueTab) => void;
}): JSX.Element {
  const blinkClass = blinking
    ? tab === 'alert'
      ? 'panel-tab-blink-alert'
      : 'panel-tab-blink-assist'
    : '';
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      data-testid={`queue-tab-${tab}`}
      data-blinking={blinking ? 'true' : undefined}
      onClick={() => onSelect(tab)}
      className={`flex flex-1 items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold uppercase tracking-wider ${
        active ? 'text-slate-100' : 'text-slate-400 hover:text-slate-200'
      } ${active ? 'border-b-2 border-sky-500' : 'border-b-2 border-transparent'} ${blinkClass}`}
    >
      <span>{label}</span>
      {blinking && (
        <span
          aria-hidden
          data-testid={`queue-tab-${tab}-dot`}
          className={`h-2 w-2 rounded-full ${tab === 'alert' ? 'bg-rose-400' : 'bg-violet-400'}`}
        />
      )}
    </button>
  );
}
